import React, {
    DetailedHTMLProps,
    ButtonHTMLAttributes,
    PropsWithChildren,
    ImgHTMLAttributes,
    HTMLAttributes
} from 'react';
import { clsx } from 'clsx';

import { INPUT_MAX_LENGTH } from '../../constants/choice-button-text-area-input';

import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { TextAreaInput } from '../TextAreaInput';
import { useImagePreloadingService } from '../../context/ImageProvider';

import styles from './MultiChoiceButton.module.scss';

export interface IMultiChoiceButtonLabelProps extends DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement> {
    label: string;
}

export interface IMultiChoiceButtonImgProps extends DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>{}

export interface IMultiChoiceButtonOption extends IMultiChoiceButtonLabelProps {
    value: string;
    userAnswer?: string;
    setUserAnswer?: (text: string) => void;
    maxLength?: number;
    image?: string;
}

export interface IMultiChoiceButtonProps extends DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>{
    option: IMultiChoiceButtonOption;
    selected: boolean;
}

export interface IMultiChoice {
    options: IMultiChoiceButtonOption[];
    selectedOptions: IMultiChoiceButtonOption[];
    deselectWhenValues?: string[];
    disabledWhenValues?: string[];
    onSelect: (newOptions: IMultiChoiceButtonOption[]) => void;
    buttonWrapperClass?: string;
}

export const MultiChoicesButtonWrapper = ({ children }: PropsWithChildren) => <div className={styles.multiChoicesWrapper}> {children} </div>;

export const MultiChoiceButton = ({ children, option, selected, className, ...props }: IMultiChoiceButtonProps) => {

    const textAreaShown = selected && option.setUserAnswer;
    const maxLength = option.maxLength || INPUT_MAX_LENGTH;

    return (
        <div className={clsx(className)}>
            <Button
                wrapperClass={styles.wrapper}
                buttonClass={clsx(styles.multiChoiceButton, selected && styles.multiChoiceSelected)}
                {...props}>
                    {children}
            </Button>

            {textAreaShown && <TextAreaInput
                wrapperClass={styles.textAreaInputWrapper}
                placeholder={option.placeholder || ''}
                onChange={(event) => option.setUserAnswer ? option.setUserAnswer(event.target.value) : () => { console.log('!!! Not called'); }}
                value={option.userAnswer || ''}
                maxLength={maxLength} />
            }
        </div>
    );
};

export const MultiChoicesButtonLabel = ({ label }: IMultiChoiceButtonLabelProps) => <span className={styles.multiChoiceText}>{label}</span>;

export const MultiChoicesButtonImg = ({ src, alt }: IMultiChoiceButtonImgProps) => <img src={src} alt={alt} className={styles.multiChoiceImg} />;

export const MultiChoice = ({ options, selectedOptions, deselectWhenValues = [], disabledWhenValues = [], onSelect, buttonWrapperClass }: IMultiChoice) => {

    const { getImageByUrl } = useImagePreloadingService();

    // Remove item or add
    const handleClick = (option: IMultiChoiceButtonOption) => {
        let newSelectedOptions = [...selectedOptions];

        if (deselectWhenValues.includes(option.value)) {
            newSelectedOptions = [];
        } else {
            newSelectedOptions = selectedOptions.filter((i: IMultiChoiceButtonOption) => !deselectWhenValues.includes(i.value));
        }

        if (selectedOptions.map(o => o.value).includes(option.value)) {
            newSelectedOptions = selectedOptions.filter(o => o.value !== option.value);
        } else {
            newSelectedOptions.push(option);
        }

        onSelect(newSelectedOptions);
    };

    return (
        <MultiChoicesButtonWrapper>
            {options.map((option) => {
                const isSelected = selectedOptions.some(o => o.value === option.value);
                const isDisabled = selectedOptions.some(o => disabledWhenValues.includes(o.value) && o.value !== option.value);
                const image = option.image ? getImageByUrl(option.image) : null;

                return <MultiChoiceButton
                    key={option.value}
                    option={option}
                    selected={isSelected}
                    disabled={isDisabled}
                    onClick={() => handleClick(option)}
                    className={buttonWrapperClass}>
                    <Checkbox checked={isSelected} />
                    <MultiChoicesButtonLabel label={option.label} />
                    {image && <MultiChoicesButtonImg src={image} alt={option.value}/>}
                </MultiChoiceButton>
            })}
        </MultiChoicesButtonWrapper>
    );
};
